import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, Edit, Trash2, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const reminderContentSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  videoUrl: z.string().url("Please enter a valid URL").optional().or(z.literal("")),
  emailSubject: z.string().min(1, "Email subject is required"),
  emailBody: z.string().min(1, "Email body is required"),
  reminderType: z.enum(["10_days_before", "1_day_before"]),
  isActive: z.boolean().default(true),
});

type ReminderContentForm = z.infer<typeof reminderContentSchema>;

interface ReminderContent {
  id: string;
  title: string;
  description?: string;
  videoUrl?: string;
  emailSubject: string;
  emailBody: string;
  reminderType: "10_days_before" | "1_day_before";
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export default function AdminReminderContent() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingContent, setEditingContent] = useState<ReminderContent | null>(null);

  const form = useForm<ReminderContentForm>({
    resolver: zodResolver(reminderContentSchema),
    defaultValues: {
      title: "",
      description: "",
      videoUrl: "",
      emailSubject: "",
      emailBody: "",
      reminderType: "10_days_before",
      isActive: true,
    },
  });

  const { data: content, isLoading } = useQuery({
    queryKey: ['/admin/reminder-content'],
    queryFn: async () => {
      const response = await apiRequest("GET", "/admin/reminder-content");
      return response.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: ReminderContentForm) => {
      const response = await apiRequest("POST", "/admin/reminder-content", data);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Reminder content created successfully",
      });
      queryClient.invalidateQueries({ queryKey: ['/admin/reminder-content'] });
      setIsDialogOpen(false);
      form.reset();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: ReminderContentForm) => {
      const response = await apiRequest("PUT", `/admin/reminder-content/${editingContent?.id}`, data);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Reminder content updated successfully",
      });
      queryClient.invalidateQueries({ queryKey: ['/admin/reminder-content'] });
      setIsDialogOpen(false);
      setEditingContent(null);
      form.reset();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("DELETE", `/admin/reminder-content/${id}`);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Reminder content deleted successfully",
      });
      queryClient.invalidateQueries({ queryKey: ['/admin/reminder-content'] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: ReminderContentForm) => {
    if (editingContent) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (content: ReminderContent) => {
    setEditingContent(content);
    form.reset({
      title: content.title,
      description: content.description || "",
      videoUrl: content.videoUrl || "",
      emailSubject: content.emailSubject,
      emailBody: content.emailBody,
      reminderType: content.reminderType,
      isActive: content.isActive,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    if (confirm("Are you sure you want to delete this reminder content?")) {
      deleteMutation.mutate(id);
    }
  };

  const handleCreate = () => {
    setEditingContent(null);
    form.reset();
    setIsDialogOpen(true);
  };

  const handleTestReminders = async () => {
    try {
      const response = await apiRequest("POST", "/admin/trigger-reminders");
      if (response.ok) {
        toast({
          title: "Success",
          description: "Reminders triggered successfully! Check server logs for details.",
        });
      } else {
        throw new Error("Failed to trigger reminders");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to trigger reminders",
        variant: "destructive",
      });
    }
  };

  const getReminderTypeLabel = (type: string) => {
    return type === "10_days_before" ? "10 Days Before Joining" : "1 Day Before Joining";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reminder Content Management</h1>
          <p className="text-gray-600 mt-2">
            Manage email reminder content for candidates before they join Servify
          </p>
        </div>
        <div className="flex space-x-3">
          <Button onClick={handleCreate} className="bg-servify-purple hover:bg-servify-purple-dark">
            <Plus className="w-4 h-4 mr-2" />
            Add Content
          </Button>
          <Button 
            onClick={handleTestReminders} 
            variant="outline"
            className="border-green-600 text-green-600 hover:bg-green-50"
          >
            🧪 Test Reminders
          </Button>
        </div>
      </div>

      <div className="grid gap-6">
        {content?.content?.map((item: ReminderContent) => (
          <Card key={item.id} className="shadow-lg">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-xl">{item.title}</CardTitle>
                  <CardDescription className="text-gray-600">
                    {getReminderTypeLabel(item.reminderType)}
                  </CardDescription>
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(item)}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(item.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {item.description && (
                  <div>
                    <h4 className="font-semibold text-gray-700">Description:</h4>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                )}
                
                {item.videoUrl && (
                  <div>
                    <h4 className="font-semibold text-gray-700">Video URL:</h4>
                    <a 
                      href={item.videoUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      {item.videoUrl}
                    </a>
                  </div>
                )}

                <div>
                  <h4 className="font-semibold text-gray-700">Email Subject:</h4>
                  <p className="text-gray-600">{item.emailSubject}</p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-700">Email Body:</h4>
                  <p className="text-gray-600 bg-gray-50 p-3 rounded border">
                    {item.emailBody}
                  </p>
                </div>

                <div className="flex items-center space-x-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    item.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {item.isActive ? 'Active' : 'Inactive'}
                  </span>
                  <span className="text-xs text-gray-500">
                    Updated: {new Date(item.updatedAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {(!content?.content || content.content.length === 0) && (
          <Card className="text-center py-12">
            <CardContent>
              <p className="text-gray-500 text-lg">No reminder content found</p>
              <p className="text-gray-400 mt-2">Create your first reminder content to get started</p>
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingContent ? "Edit Reminder Content" : "Create Reminder Content"}
            </DialogTitle>
            <DialogDescription>
              Configure email reminder content for candidates. This content will be sent automatically based on their joining date.
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter content title" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="reminderType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Reminder Type</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select reminder type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="10_days_before">10 Days Before Joining</SelectItem>
                          <SelectItem value="1_day_before">1 Day Before Joining</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description (Optional)</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Enter content description" 
                        {...field} 
                        rows={3}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="videoUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Video URL (Optional)</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="https://example.com/video.mp4" 
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="emailSubject"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Subject</FormLabel>
                    <FormControl>
                      <Input 
                        placeholder="Welcome to Servify - Your Journey Begins!" 
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="emailBody"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Body</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Enter the email body content..." 
                        {...field} 
                        rows={8}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="bg-servify-purple hover:bg-servify-purple-dark"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? "Saving..."
                    : editingContent
                    ? "Update Content"
                    : "Create Content"
                  }
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
